import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
    forgetAccountOnThisDevice,
    hasAccountOnThisDevice,
    looksLikeEmailSignInLink,
    rememberAccountOnThisDevice,
} from './accountPresence';

/**
 * The shared account, as a context (SPEC-009 PR-9.3, ADR-0005).
 *
 * Adapted from `~/projects/plant.habb.me/src/application/account/AuthProvider.js`,
 * which was adapted from habb.me's `useCloudAuth`: same providers, same tenant,
 * so a habb.me or plant.habb.me account is already an account here (ADR-0003).
 * Two departures carried over from plant because they were right there too:
 *
 *   - Errors are carried as message KEYS, never English strings. Every word a
 *     visitor sees goes through `t()` (the repo contract), and a hook has no
 *     business choosing a language.
 *   - No `window.prompt` to recover a missing email-link address. A link opened
 *     on a device that never asked for one gets a stated error and a working
 *     form, not a browser dialog that cannot be translated.
 *
 * And one that is this product's own: **nothing here gates anything.** Legends
 * is a catalogue and a card game that work signed out, offline and uninstalled,
 * and sign-in exists to put a run on a shared board (SPEC-009). So every failure
 * path in this file — the SDK not loading, a blocked popup, a rejected token —
 * lands on `signed-out` with the product intact, and never on a state a screen
 * has to apologise for. The default context value is a resolved guest for the
 * same reason: a tree rendered without the provider is simply a signed-out tree,
 * which is what most of this app's unit tests want and all of them get for free.
 */

const IDLE_EMAIL_LINK_STATE = { status: 'idle', pendingEmail: '', errorKey: null };

const GUEST_AUTH = {
    user: null,
    authStatus: 'signed-out',
    isAuthResolved: true,
    isAuthenticated: false,
    signIn: () => Promise.resolve(),
    signOut: () => Promise.resolve(),
    sendEmailLink: () => Promise.resolve(),
    emailLinkState: IDLE_EMAIL_LINK_STATE,
};

const AuthContext = createContext(GUEST_AUTH);

function loadAuthContext() {
    return import('../../infrastructure/cloud/firebaseAuthClient').then(({ getAuthContext }) => getAuthContext());
}

/**
 * A name for somebody whose provider did not supply one.
 *
 * The email link route has no display name at all — there is no profile behind
 * an email address — so the local part stands in. This is the whole reason the
 * board's name is allowed to be "the provider's name" without a text field
 * (SPEC-009 D-3): every route produces something drawable, so there is never a
 * moment where the reader has to be asked.
 */
function buildFallbackName(email) {
    if (typeof email !== 'string' || !email) {
        return null;
    }

    const [localPart] = email.split('@');

    return localPart || email;
}

function createUser(firebaseUser) {
    return {
        uid: firebaseUser.uid,
        name: firebaseUser.displayName || buildFallbackName(firebaseUser.email),
        email: firebaseUser.email,
    };
}

async function ensureUserSessionReady(firebaseUser) {
    if (firebaseUser && typeof firebaseUser.getIdToken === 'function') {
        await firebaseUser.getIdToken();
    }

    return firebaseUser;
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [authStatus, setAuthStatus] = useState('loading');
    const [emailLinkState, setEmailLinkState] = useState(IDLE_EMAIL_LINK_STATE);

    useEffect(() => {
        let cancelled = false;
        let unsubscribe = () => {};

        /**
         * The SDK is not loaded to discover that there is nothing to discover.
         *
         * A device that has never signed in is a guest, and asking Firebase to
         * confirm it would cost every such reader a chunk and a third-party
         * origin for a feature they have not opened (SPEC-009 D-10). The one
         * exception is an address that looks like an emailed sign-in link, which
         * is how a reader arrives on a device whose flag is not set yet.
         */
        if (!hasAccountOnThisDevice()
            && !looksLikeEmailSignInLink(typeof window === 'undefined' ? '' : window.location?.href)) {
            setAuthStatus('signed-out');
            setUser(null);

            return undefined;
        }

        loadAuthContext()
            .then((authContext) => {
                if (cancelled) {
                    return;
                }

                const {
                    auth,
                    onAuthStateChanged,
                    isSignInWithEmailLink,
                    signInWithEmailLink,
                    readPendingEmailLinkAddress,
                    clearPendingEmailLinkAddress,
                    clearEmailLinkFromLocation,
                } = authContext;

                if (typeof isSignInWithEmailLink === 'function'
                    && typeof window !== 'undefined'
                    && isSignInWithEmailLink(auth, window.location.href)) {
                    const pendingEmail = readPendingEmailLinkAddress();

                    if (pendingEmail) {
                        setEmailLinkState({ status: 'completing', pendingEmail, errorKey: null });

                        signInWithEmailLink(auth, pendingEmail, window.location.href)
                            .then(() => {
                                if (cancelled) {
                                    return;
                                }

                                clearPendingEmailLinkAddress();
                                clearEmailLinkFromLocation();
                                setEmailLinkState(IDLE_EMAIL_LINK_STATE);
                            })
                            .catch((error) => {
                                console.error(error);

                                if (!cancelled) {
                                    setEmailLinkState({
                                        status: 'error',
                                        pendingEmail,
                                        errorKey: 'account.email.error.complete',
                                    });
                                }
                            });
                    } else {
                        // The link was opened on a device that never requested
                        // one, so there is no stored address to finish with.
                        setEmailLinkState({
                            status: 'error',
                            pendingEmail: '',
                            errorKey: 'account.email.error.differentDevice',
                        });
                    }
                }

                unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
                    if (!firebaseUser) {
                        // The flag was a hint and Firebase has disagreed with
                        // it — an expired or revoked session. Correcting it here
                        // is what stops this device loading the SDK on every
                        // future visit to answer the same question.
                        forgetAccountOnThisDevice();
                        setAuthStatus('signed-out');
                        setUser(null);
                        return;
                    }

                    rememberAccountOnThisDevice();

                    setAuthStatus('loading');

                    ensureUserSessionReady(firebaseUser)
                        .then(() => {
                            if (!cancelled) {
                                setAuthStatus('authenticated');
                                setUser(createUser(firebaseUser));
                            }
                        })
                        .catch((error) => {
                            console.error(error);

                            if (!cancelled) {
                                setAuthStatus('signed-out');
                                setUser(null);
                            }
                        });
                });
            })
            .catch((error) => {
                // Auth failing to load must never take the shell down — and on
                // this product it must not even be visible. The whole app works
                // signed out, so a reader whose network ate the SDK gets the
                // game and the device board, which is what they had yesterday.
                console.error(error);

                if (!cancelled) {
                    setAuthStatus('signed-out');
                    setUser(null);
                }
            });

        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, []);

    const value = useMemo(() => {
        function signIn() {
            setAuthStatus('loading');

            return loadAuthContext()
                .then(({ auth, provider, signInWithPopup }) => signInWithPopup(auth, provider))
                .then((result) => ensureUserSessionReady(result.user).then(() => {
                    rememberAccountOnThisDevice();
                    setAuthStatus('authenticated');
                    setUser(createUser(result.user));
                }))
                .catch((error) => {
                    // A closed popup and a real failure end the same way: still
                    // signed out, and the button still there. A reader who
                    // changed their mind mid-popup has not made a mistake and
                    // must not be shown an error for it.
                    console.error(error);
                    setAuthStatus('signed-out');
                });
        }

        function sendEmailLink(email) {
            const normalizedEmail = typeof email === 'string' ? email.trim() : '';

            if (!normalizedEmail) {
                setEmailLinkState({ status: 'error', pendingEmail: '', errorKey: 'account.email.error.empty' });

                return Promise.resolve();
            }

            setEmailLinkState({ status: 'sending', pendingEmail: normalizedEmail, errorKey: null });

            return import('../../infrastructure/cloud/firebaseAuthClient')
                .then((firebaseAuth) => firebaseAuth.getAuthContext().then((authContext) => authContext
                    .sendSignInLinkToEmail(authContext.auth, normalizedEmail, firebaseAuth.createEmailLinkActionCodeSettings())
                    .then(() => firebaseAuth.storePendingEmailLinkAddress(normalizedEmail))))
                .then(() => {
                    // Set when the link is REQUESTED, not when it is redeemed:
                    // the link comes back to this device, and the flag is what
                    // makes the returning page load the SDK to finish the job.
                    rememberAccountOnThisDevice();
                    setEmailLinkState({ status: 'sent', pendingEmail: normalizedEmail, errorKey: null });
                })
                .catch((error) => {
                    console.error(error);
                    setEmailLinkState({
                        status: 'error',
                        pendingEmail: normalizedEmail,
                        errorKey: 'account.email.error.send',
                    });
                });
        }

        function signOut() {
            setAuthStatus('loading');

            return loadAuthContext()
                .then(({ auth, signOut: signOutUser }) => signOutUser(auth))
                .then(() => {
                    forgetAccountOnThisDevice();
                    setAuthStatus('signed-out');
                    setUser(null);
                })
                .catch((error) => {
                    console.error(error);
                    setAuthStatus('signed-out');
                });
        }

        return {
            user,
            authStatus,
            isAuthResolved: authStatus !== 'loading',
            isAuthenticated: authStatus === 'authenticated' && Boolean(user),
            signIn,
            signOut,
            sendEmailLink,
            emailLinkState,
        };
    }, [user, authStatus, emailLinkState]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    return useContext(AuthContext);
}
